import { ManipulateType } from 'dayjs';

import { CalendarItemModel } from 'shared/models';
import { observable, toDate } from 'shared/utilities';
import { ITaskModel } from 'shared/models/TaskTracker';

import { CALENDAR_PAGE } from './constants/CALENDAR_PAGE';

export const Store = observable({
    isLoading: false,
    _calendarItems: [] as CalendarItemModel[],
    date: toDate().set('hour', 12),
    view: 'week' as keyof typeof CALENDAR_PAGE.VIEWS,

    taskTrackerTask: null as ITaskModel | null,

    filter: observable({
        noWeekends: false,
        onlyOrganizer: false,
        meeting: true,
        education: true,
        reminder: true,
        task: true,
    }),

    edit: observable({
        isOpened: false,
        calendarItem: new CalendarItemModel(),
    }),

    toParams: () => {
        return {
            date: Store.date.format('YYYY-MM-DD'),
            view: Store.view,
            filter: JSON.stringify(Store.filter),
        };
    },

    fromParams: (params: { date?: string, view?: keyof typeof CALENDAR_PAGE.VIEWS, filter?: typeof Store['filter'] }) => {
        if (params.date) {
            Store.set('date', toDate(params.date));
        }
        if (params.view) {
            Store.set('view', params.view);
        }
        if (params.filter) {
            Store.filter.update(params.filter);
        }
    },

    set calendarItems(calendarItems: CalendarItemModel[]) {
        this._calendarItems = calendarItems;
    },

    get calendarItems() {
        return this._calendarItems.filter((calendarItem: CalendarItemModel) => {
            if (this.filter.onlyOrganizer) {
                if (!calendarItem.isOrganizer) {
                    console.log(calendarItem);
                }
                return calendarItem.isOrganizer;
            }
            return true;
        }).filter((calendarItem: CalendarItemModel) => {
            return this.filter[calendarItem.typeId] ?? true;
        });
    },

    next: () => {
        const modifier = 1;
        let unit: ManipulateType = 'day';
        switch (Store.view) {
            case 'week':
                unit = 'week';
                break;
            case 'month':
                unit = 'month';
                break;
            case 'year':
                unit = 'year';
                break;
        }

        const nextDay = Store.date.add(modifier, unit);

        if (Store.filter.noWeekends && nextDay.day() == 6 || nextDay.day() == 0) {
            switch (nextDay.day()) {
                case 6:
                    Store.set('date', Store.date.add(3, unit));
                    break;
                case 0:
                    Store.set('date', Store.date.add(2, unit));
                    break;
            }
        } else {
            Store.set('date', nextDay);
        }
    },

    prev: function() {
        const modifier = 1;
        let unit: ManipulateType = 'day';
        switch (Store.view) {
            case 'week':
                unit = 'week';
                break;
            case 'month':
                unit = 'month';
                break;
            case 'year':
                unit = 'year';
                break;
        }

        const prevDay = Store.date.subtract(modifier, unit);

        if (Store.filter.noWeekends && prevDay.day() == 6 || prevDay.day() == 0) {
            switch (prevDay.day()) {
                case 6:
                    Store.set('date', Store.date.subtract(2, unit));
                    break;
                case 0:
                    Store.set('date', Store.date.subtract(3, unit));
                    break;
            }
        } else {
            Store.set('date', prevDay);
        }
    },

    getDaysForView: function() {
        const days = [];
        let current = this.date.clone();

        switch (this.view) {
            case 'week':
                current = this.date.startOf('week');
                for (let i = 0; i < 7; i++) {
                    if(this.filter.noWeekends && (current.day() === 0 || current.day() === 6)){
                        current = current.add(1, 'day');
                        continue;
                    }
                    days.push(current.clone());
                    current = current.add(1, 'day');
                }
                break;
            case 'days':
                // Если включён фильтр "скрыть выходные" и отображение на 3 дня
                if (this.filter.noWeekends) {
                    current = this.date.startOf('day');

                    // Проверяем, является ли текущий день будним
                    if (current.day() !== 0 && current.day() !== 6) {
                        if (current.day() === 5) { // Пятница (moment.js: 0=вс, 1=пн, ..., 5=пт)
                            // Для пятницы показываем: четверг, пятницу, понедельник
                            let thursday = current.clone().subtract(1, 'day'); // четверг
                            let friday = current.clone(); // пятница
                            let monday = current.clone().add(3, 'day'); // следующий понедельник

                            days.push(thursday);
                            days.push(friday);
                            days.push(monday);
                        } else if (current.day() === 1) { // Понедельник
                            // Для понедельника показываем: пятницу, понедельник, вторник
                            let friday = current.clone().subtract(3, 'day'); // предыдущая пятница
                            let monday = current.clone(); // понедельник
                            let tuesday = current.clone().add(1, 'day'); // вторник

                            days.push(friday);
                            days.push(monday);
                            days.push(tuesday);
                        } else {
                            // Для остальных будних дней (вт, ср, чт) показываем предыдущий, текущий, следующий
                            let prevDay = current.clone().subtract(1, 'day');
                            while (prevDay.day() === 0 || prevDay.day() === 6) {
                                prevDay = prevDay.subtract(1, 'day');
                            }

                            let nextDay = current.clone().add(1, 'day');
                            while (nextDay.day() === 0 || nextDay.day() === 6) {
                                nextDay = nextDay.add(1, 'day');
                            }

                            days.push(prevDay);
                            days.push(current);
                            days.push(nextDay);
                        }
                    } else {
                        // Если текущий день выходной, используем стандартную логику
                        current = this.date.startOf('day').subtract(1, 'day');
                        for (let i = 0; i < 3; i++) {
                            if(this.filter.noWeekends && (current.day() === 0 || current.day() === 6)){
                                current = current.add(1, 'day');
                                continue;
                            }
                            days.push(current.clone());
                            current = current.add(1, 'day');
                        }
                    }
                } else {
                    // Без фильтра "скрыть выходные" - используем стандартную логику
                    current = this.date.startOf('day').subtract(1, 'day');
                    for (let i = 0; i < 3; i++) {
                        days.push(current.clone());
                        current = current.add(1, 'day');
                    }
                }
                // Сортируем дни по возрастанию дат
                days.sort((a, b) => a.valueOf() - b.valueOf());
                break;
            case 'day':
                days.push(this.date.clone());
                break;
        }
        return days;
    }
});
